import {
    Inventory2Outlined,
    PriceCheckOutlined,
    PrintOutlined,
    PaymentsOutlined,
    PercentOutlined,
    ReceiptLongOutlined,
    AccountBalanceWalletOutlined,
    SwapVertOutlined,
    PointOfSaleOutlined,
    RuleOutlined,
    HourglassBottomOutlined,
    LocalFireDepartmentOutlined,
    Inventory2 as InventoryFilled,
    PersonOutline,
    VisibilityOutlined,
    CheckCircleOutline,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import FlowDiagram, { FlowNode } from './FlowDiagram';
import ContractLifecycleDiagram from './ContractLifecycleDiagram';

/** New-contract three-step flow. */
const NewContractFlow = () => {
    const { t } = useTranslation();
    const nodes: FlowNode[] = [
        { label: t('helpDiagrams.newContract.step1'), icon: <Inventory2Outlined fontSize="small" />, color: 'primary' },
        { label: t('helpDiagrams.newContract.step2'), icon: <PriceCheckOutlined fontSize="small" />, color: 'info' },
        { label: t('helpDiagrams.newContract.step3'), icon: <PrintOutlined fontSize="small" />, color: 'success' },
    ];
    return <FlowDiagram title={t('helpDiagrams.newContract.title')} nodes={nodes} />;
};

/** Interest -> redeem total, with the extend-only alternative as caption. */
const RedemptionFlow = () => {
    const { t } = useTranslation();
    const nodes: FlowNode[] = [
        { label: t('helpDiagrams.redemption.loan'), icon: <PaymentsOutlined fontSize="small" />, color: 'primary' },
        { label: t('helpDiagrams.redemption.interest'), icon: <PercentOutlined fontSize="small" />, color: 'info' },
        { label: t('helpDiagrams.redemption.total'), icon: <ReceiptLongOutlined fontSize="small" />, color: 'success' },
    ];
    return (
        <FlowDiagram
            title={t('helpDiagrams.redemption.title')}
            nodes={nodes}
            caption={t('helpDiagrams.redemption.extend')}
        />
    );
};

/** Cash handover sequence. */
const HandoverFlow = () => {
    const { t } = useTranslation();
    const nodes: FlowNode[] = [
        { label: t('helpDiagrams.handover.opening'), icon: <AccountBalanceWalletOutlined fontSize="small" />, color: 'primary' },
        { label: t('helpDiagrams.handover.movements'), icon: <SwapVertOutlined fontSize="small" />, color: 'info' },
        { label: t('helpDiagrams.handover.count'), icon: <PointOfSaleOutlined fontSize="small" />, color: 'warning' },
        { label: t('helpDiagrams.handover.difference'), icon: <RuleOutlined fontSize="small" />, color: 'success' },
    ];
    return <FlowDiagram title={t('helpDiagrams.handover.title')} nodes={nodes} />;
};

/** Precious-metal flow from expired metal to refined stock. */
const MetalFlow = () => {
    const { t } = useTranslation();
    const nodes: FlowNode[] = [
        { label: t('helpDiagrams.metal.expired'), icon: <HourglassBottomOutlined fontSize="small" />, color: 'warning' },
        { label: t('helpDiagrams.metal.melting'), icon: <LocalFireDepartmentOutlined fontSize="small" />, color: 'error' },
        { label: t('helpDiagrams.metal.stock'), icon: <InventoryFilled fontSize="small" />, color: 'success' },
    ];
    return <FlowDiagram title={t('helpDiagrams.metal.title')} nodes={nodes} />;
};

/** Shift-verification sequence. */
const VerifyFlow = () => {
    const { t } = useTranslation();
    const nodes: FlowNode[] = [
        { label: t('helpDiagrams.verify.own'), icon: <PersonOutline fontSize="small" />, color: 'primary' },
        { label: t('helpDiagrams.verify.previous'), icon: <VisibilityOutlined fontSize="small" />, color: 'info' },
        { label: t('helpDiagrams.verify.confirm'), icon: <CheckCircleOutline fontSize="small" />, color: 'success' },
    ];
    return <FlowDiagram title={t('helpDiagrams.verify.title')} nodes={nodes} />;
};

const registry: Record<string, () => JSX.Element> = {
    contractLifecycle: ContractLifecycleDiagram,
    stepFlow: NewContractFlow,
    redemptionInterest: RedemptionFlow,
    cashHandover: HandoverFlow,
    metalFlow: MetalFlow,
    shiftVerification: VerifyFlow,
};

interface HelpIllustrationProps {
    illustration?: string;
}

/** Render the illustration for a topic, or nothing when the key is unknown. */
const HelpIllustration = ({ illustration }: HelpIllustrationProps) => {
    if (!illustration) return null;
    const Component = registry[illustration];
    return Component ? <Component /> : null;
};

export default HelpIllustration;
